import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSync, faExternalLinkAlt, faCopy, faCheck, faPhone } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../../AuthContext';
import { providerStatusClass, parseProviderResponse } from '../../orderStatus';
import Modal from '../../components/Modal';
import './AdminOrders.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const POLL_INTERVAL = 3 * 60 * 1000;

const statusColors = {
  completed: 'status-completed',
  processing: 'status-processing',
  pending: 'status-pending',
  cancelled: 'status-cancelled',
};

function OrderLinkCell({ link }) {
  const [copied, setCopied] = useState(false);

  if (!link) return <span>—</span>;

  const safeUrl = link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="order-link-cell">
      <a
        href={safeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="order-link-anchor"
        title={link}
      >
        <span className="order-link-text">{link}</span>
        <FontAwesomeIcon icon={faExternalLinkAlt} className="order-link-icon" />
      </a>
      <button
        type="button"
        className={`order-link-copy-btn ${copied ? 'copied' : ''}`}
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy Link'}
        aria-label="Copy Link"
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied && <span className="copied-tooltip">Copied!</span>}
      </button>
    </div>
  );
}

export default function AdminOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });
  const searchRef = useRef(search);
  const statusRef = useRef(statusFilter);

  searchRef.current = search;
  statusRef.current = statusFilter;

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(() => {
    const params = new URLSearchParams();
    if (statusRef.current) params.set('status', statusRef.current);
    if (searchRef.current) params.set('search', searchRef.current);

    fetch(`${API}/admin/orders?${params}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  useEffect(() => {
    if (!search) return;
    const timer = setTimeout(fetchOrders, 300);
    return () => clearTimeout(timer);
  }, [search, fetchOrders]);

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API}/admin/orders/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status }),
    });
    const data = await res.json();
    if (!res.ok) {
      setModal({ open: true, type: 'error', title: 'Update Failed', message: data.message || 'Could not update order status.' });
    }
    fetchOrders();
  };

  const syncStatuses = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/admin/orders/sync-status`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      setModal({ open: true, type: data.error ? 'error' : 'success', title: 'Status Sync', message: data.message || 'Done' });
    } catch {
      setModal({ open: true, type: 'error', title: 'Sync Failed', message: 'Could not sync order statuses.' });
    } finally {
      setSyncing(false);
      fetchOrders();
    }
  };

  return (
    <div className="admin-orders">
      <div className="orders-filters">
        {lastUpdated && (
          <span className="orders-last-updated">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input type="text" className="search-input" placeholder="Search by ID, user or service..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="processing">In Progress</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button className="sync-btn" onClick={syncStatuses} disabled={syncing}>
          <FontAwesomeIcon icon={faSync} className={syncing ? 'spinning' : ''} />
          {syncing ? 'Syncing...' : 'Sync Status'}
        </button>
      </div>

      <div className="admin-table-card">
        <table className="admin-orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>User</th>
              <th>Service</th>
              <th>Platform</th>
              <th>Link</th>
              <th>Qty</th>
              <th>Charge</th>
              <th>Status</th>
              <th>Provider</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const provider = parseProviderResponse(o);
              const userPhone = o.user?.phone ? o.user.phone.replace(/[^\d+]/g, '') : null;
              const waClean = userPhone ? userPhone.replace(/\D/g, '') : null;

              return (
              <tr key={o.id}>
                <td className="id-cell">#{o.id}</td>
                <td className="admin-order-user-cell">
                  <div className="order-user-name">{o.user?.name || '—'}</div>
                  {userPhone && (
                    <div className="order-user-contact">
                      <span className="order-user-phone">{userPhone}</span>
                      <a
                        href={`https://wa.me/${waClean}?text=Hello%20${encodeURIComponent(o.user?.name || 'Customer')}%2C%20regarding%20Order%20%23${o.id}%3A`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="order-user-wa-btn"
                        title="Chat on WhatsApp"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </a>
                      <a
                        href={`tel:${userPhone}`}
                        className="order-user-call-btn"
                        title="Call User"
                      >
                        <FontAwesomeIcon icon={faPhone} />
                      </a>
                    </div>
                  )}
                </td>
                <td>{o.service?.name || '—'}</td>
                <td><span className="platform-badge">{o.service?.platform || '—'}</span></td>
                <td className="admin-order-link-td">
                  <OrderLinkCell link={o.link} />
                </td>
                <td>{Number(o.quantity).toLocaleString()}</td>
                <td>TSHS {Number(o.charge).toLocaleString()}</td>
                <td>
                  <select
                    className={`status-select ${statusColors[o.status] || ''}`}
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="provider-cell">
                  {provider ? (
                    <>
                      <span className={`provider-badge ${providerStatusClass(provider.status)}`}>
                        {provider.status || '—'}
                      </span>
                      <div className="provider-meta">
                        {Object.entries(provider)
                          .filter(([k]) => k !== 'status')
                          .map(([k, v]) => (
                            <span key={k}>{k.replace(/_/g, ' ')}: {v}</span>
                          ))}
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="date-cell">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
              );
            })}
            {orders.length === 0 && (
              <tr>
                <td colSpan={10} className="empty-cell">No orders found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={modal.open} type={modal.type} title={modal.title} message={modal.message} onClose={() => setModal({ ...modal, open: false })} />
    </div>
  );
}
